/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { FaFileInvoice, FaCheck, FaFileCsv, FaFilter } from 'react-icons/fa';
import BulkPaymentsTennatRow from './_bulkpaymentstenantrow';
import { Button, Form, InputGroup, Placeholder, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getConfig, serverUrl } from '../../../../serverUrl';
import { CSVLink } from 'react-csv';

// Date Filter Modal Component
const DateFilterModal = ({ show, onHide, onApply, filterType }) => {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const handleApply = () => {
    if (filterType === 'month' && (!selectedMonth || !selectedYear)) {
      toast.warning('Please select both month and year');
      return;
    }
    if (filterType === 'year' && !selectedYear) {
      toast.warning('Please select year');
      return;
    }
    onApply(filterType === 'month' ? `${selectedYear}-${selectedMonth}` : selectedYear.toString());
    onHide();
  };

  const handleClear = () => {
    setSelectedMonth('');
    setSelectedYear('');
    onApply(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Filter by {filterType}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {filterType === 'month' && (
          <Form.Group className="mb-3">
            <p>Month</p>
            <Form.Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">Select Month</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        <Form.Group>
          <p>Year</p>
          <Form.Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="">Select Year</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClear}>
          Clear Filter
        </Button>
        <Button variant="primary" onClick={handleApply}>
          Apply Filter
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const BulkPaymentsTable = ({
  approvalData,
  isLoading,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [apartmentFilter, setApartmentFilter] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('approval');
  const [isApproving, setIsApproving] = useState(false);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [approvalStatusFilter, setApprovalStatusFilter] = useState('all');
  const [showMonthFilterModal, setShowMonthFilterModal] = useState(false);
  const [showYearFilterModal, setShowYearFilterModal] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [filterType, setFilterType] = useState('month');
  const [csvData, setCsvData] = useState([]);

  const itemsPerPage = 10;
  const ConfigData = getConfig();

  // Get unique apartment names for filter dropdown
  const apartmentOptions = approvalData
    ? Array.from(
      new Set(
        approvalData
          .map(t => t.apartment)
          .filter(Boolean)
      )
    )
    : [];

  // Filter tenants based on search term, apartment filter, and date filter
  const filteredTenants = approvalData
    ? approvalData
      .filter(tenant => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          tenant.name?.toLowerCase()?.includes(searchLower) ||
          (tenant.phone_number && tenant.phone_number.includes(searchTerm)) ||
          (tenant.transaction_id && tenant.transaction_id?.toLowerCase()?.includes(searchLower)) ||
          // (tenant.paid_amount && tenant.paid_amount?.toLowerCase()?.includes(searchLower)) ||
          // (tenant.invoice_month && tenant.invoice_month?.toLowerCase()?.includes(searchLower)) ||
          (tenant.payment_method && tenant.payment_method?.toLowerCase()?.includes(searchLower)) ||
          (tenant.house_number && tenant.house_number?.toLowerCase()?.includes(searchLower)) ||
          (tenant.apartment && tenant.apartment?.toLowerCase()?.includes(searchLower)) ||
          (tenant.kra_pin && tenant.kra_pin.toString().includes(searchTerm)) ||
          (tenant.approval_status && tenant.approval_status.toString().includes(searchTerm));

        const matchesApartment = !apartmentFilter || tenant.apartment === apartmentFilter;
        const matchesApprovalStatus =
          approvalStatusFilter === 'all' ||
          (approvalStatusFilter === 'approved' && tenant.approval_status === 'approved') ||
          (approvalStatusFilter === 'pending' && tenant.approval_status === 'pending');

        // Date filtering logic
        let matchesDate = true;
        if (dateFilter) {
          try {
            const paymentDate = new Date(tenant.payment_date);

            if (filterType === 'month') {
              const filterDate = new Date(`${dateFilter}-01`); 
              matchesDate =
                paymentDate.getFullYear() === filterDate.getFullYear() &&
                paymentDate.getMonth() === filterDate.getMonth(); 
            } else if (filterType === 'year') {
              matchesDate = paymentDate.getFullYear() === parseInt(dateFilter);
            }
          } catch (e) {
            console.error('Error parsing date:', e);
            matchesDate = false;
          }

        }

        return matchesSearch && matchesApartment && matchesApprovalStatus && matchesDate;
      })
      .sort((a, b) => {
        return (a.approved === b.approved) ? 0 : a.approved ? 1 : -1;
      })
    : [];

  // Prepare CSV data whenever filtered tenants change
  useEffect(() => {
    if (filteredTenants && filteredTenants.length > 0) {
      const data = filteredTenants.map(tenant => ({
        'Tenant Name': tenant.name,
        'Approval Status': tenant.approval_status,
        'Phone Number': tenant.phone_number,
        'House Number': tenant.house_number,
        'Paid Amount': tenant.paid_amount,
        'Payment Method': tenant.payment_method,
        'Transaction ID': tenant.transaction_id,
        'Payment Date': tenant.payment_date,
        'Invoice Amount': tenant.invoice_amount,
        'Invoice Status': tenant.invoice_status,
        'Apartment': tenant.apartment,
        'KRA PIN': tenant.kra_pin
      }));
      setCsvData(data);
    } else {
      setCsvData([]);
    }
  }, [filteredTenants]);

  // Get only pending approvals that can be selected
  const selectableTenants = filteredTenants
    ? filteredTenants.filter(tenant => tenant.approval_status !== 'approved')
    : [];

  // Calculate pagination with filtered tenants
  const totalPages = Math.ceil(filteredTenants?.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTenants?.slice(indexOfFirstItem, indexOfLastItem) || [];

  // Handle select/deselect all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedTenants([]);
    } else {
      const allSelectableIds = selectableTenants.map(tenant => tenant.id);
      setSelectedTenants(allSelectableIds);
    }
    setSelectAll(!selectAll);
  };

  // Handle individual selection
  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  // Reset select all when filters change
  useEffect(() => {
    setSelectAll(false);
    setSelectedTenants([]);
    setCurrentPage(1);
  }, [searchTerm, apartmentFilter, dateFilter, approvalStatusFilter]);

  // Change page
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const getCurrentTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };

  const handleApprovePayment = async (approvalIds) => {
    setIsApproving(true);
    const currentTime = getCurrentTime();
    let successCount = 0;
    let shouldReload = false;

    try {
      // Process each approval individually
      for (const id of approvalIds) {
        try {
          // if the statement is for approval
          if (paymentStatus === 'approval') {
            const tenantData = approvalData.find(t => t.id === id);
            if (!tenantData) continue;
            const paymentTime = tenantData.payment_date || getCurrentTime();

            // Prepare CtoB transaction data
            const ctobdata = {
              transcation_type: tenantData.payment_method,
              transcation_id: tenantData.transaction_id ? tenantData.transaction_id : `${tenantData.payment_method}${tenantData.house_number}`,
              bill_ref_number: tenantData.house_number,
              apartment: tenantData.apartment_id,
              transcation_time: paymentTime,
              transcation_amount: tenantData.paid_amount,
              phone_number: tenantData.phone_number,
              first_name: tenantData.name,
            };

            // Create CtoB transaction
            const createResponse = await serverUrl.post('/landlord/c2btranscation/', ctobdata, ConfigData);

            // Reconcile the transaction
            const reconcileTransaction = await serverUrl.get(
              `/landlord/reconcile_transactions/?c2btransaction_id=${createResponse.data.id}&house_id=${tenantData.house_id}&payment_category=${tenantData.invoice_reference}&invoice_month=${tenantData.invoice_month}&invoice_year=${tenantData.invoice_year}`,
              ConfigData
            );

            if (reconcileTransaction.status === 200) {
              const updateResponse = await serverUrl.patch(
                `/landlord/payment-approvals/${id}/`,
                { approval_status: 'approved' },
                ConfigData
              );

              if (updateResponse.status === 200) {
                successCount++;
              } else {
                console.error(`Failed to update approval status for ID ${id}`);
              }
            } else {
              console.error(`Failed to reconcile transaction for ID ${id}`);
            }
          } 
          else {
            const updateResponse = await serverUrl.patch(
              `/landlord/payment-approvals/${id}/`,
              { approval_status: 'rejected' },
              ConfigData
            );

            if (updateResponse.status === 200) {
              successCount++;
            } else {
              console.error(`Failed to update approval status for ID ${id}`);
            }
          }
        } catch (err) {
          console.error(`Error processing approval ID ${id}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully ${paymentStatus === 'approval' ? 'approved' : 'rejected'} ${successCount} payment(s)`);
        shouldReload = true;
      } else {
        toast.error(`No payments were ${paymentStatus === 'approval' ? 'approved' : 'rejected'}`);
      }
    } catch (err) {
      console.error("Payment approval failed:", err);
      toast.error(`An error occurred while ${paymentStatus === 'approval' ? 'approving' : 'rejecting'} payments.`);
    } finally {
      setIsApproving(false);
      setShowConfirmModal(false);
      setSelectedTenants([]);
      setSelectAll(false);

      // Reload only after all processing is complete
      if (shouldReload) {
        window.location.reload()
      }
    }
  };

  const handleBulkApprove = () => {
    if (selectedTenants.length === 0) {
      toast.warning("Please select at least one payment to approve");
      return;
    }
    setPaymentStatus('approval');
    setShowConfirmModal(true);
  };

  const handleBulkReject = () => {
    if (selectedTenants.length === 0) {
      toast.warning("Please select at least one payment to reject");
      return;
    }
    setPaymentStatus('rejectng');
    setShowConfirmModal(true);
  };

  const handleSingleApprove = (tenantId) => {
    setPaymentStatus('approval');
    handleApprovePayment([tenantId]);
  };

  const handleDateFilterApply = (filterValue) => {
    setDateFilter(filterValue);
    setCurrentPage(1);
  };

  const SkeletonRow = () => (
    <tr>
      {[...Array(11)].map((_, idx) => (
        <td key={idx} className="px-4 py-3">
          <Placeholder as="div" animation="glow">
            <Placeholder xs={12} className="rounded w-full h-4" />
          </Placeholder>
        </td>
      ))}
    </tr>
  );

  return (
    <div className="bg-white shadow border-0 rounded-lg">

      <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-4 px-6 py-2 border-gray-100">
        {/* Search input */}
        <InputGroup className="w-25 md:w-auto">
          <Form.Control
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            disabled={isLoading}
          />
        </InputGroup>
        <div className="flex md:flex-row flex-col md:items-center gap-2">
          {/* Apartment filter select */}
          <select
            className="focus:ring-opacity-50 px-3 py-2 border border-gray-300 focus:border-blue-400 rounded-md focus:ring focus:ring-blue-200"
            value={apartmentFilter}
            onChange={e => {
              setApartmentFilter(e.target.value);
              setCurrentPage(1);
            }}
            disabled={isLoading}
          >
            <option value="">All Properties</option>
            {apartmentOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>

          {/* Approval status filter */}
          <select
            className="focus:ring-opacity-50 px-3 py-2 border border-gray-300 focus:border-blue-400 rounded-md focus:ring focus:ring-blue-200"
            value={approvalStatusFilter}
            onChange={e => {
              setApprovalStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            disabled={isLoading}
          >
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending Approval</option>
          </select>

          {/* Date filter buttons */}
          <Button
            variant="outline-secondary"
            onClick={() => {
              setFilterType('month');
              setShowMonthFilterModal(true);
            }}
            disabled={isLoading}
          >
            {dateFilter && filterType === 'month' ? `Filter: ${dateFilter}` : 'Filter by Month'}
          </Button>

          <Button
            variant="outline-secondary"
            onClick={() => {
              setFilterType('year');
              setShowYearFilterModal(true);
            }}
            disabled={isLoading}
          >
            {dateFilter && filterType === 'year' ? `Filter: ${dateFilter}` : 'Filter by Year'}
          </Button>

          {/* Clear date filter */}
          {dateFilter && (
            <Button
              variant="outline-danger"
              onClick={() => {
                setDateFilter(null);
                setCurrentPage(1);
              }}
              disabled={isLoading}
            >
              Clear Date Filter
            </Button>
          )}

          {/* Export to CSV */}
          {filteredTenants.length > 0 && (
            <CSVLink
              data={csvData}
              filename={`payments_${new Date().toISOString().slice(0, 10)}.csv`}
              className="btn btn-warning"
            >
              Export CSV
            </CSVLink>
          )}

        
        </div>
      </div>
      <div className="px-0 py-4">
        <div className="border border-gray-100 rounded-lg overflow-x-auto">
          <table className="divide-y divide-gray-200 min-w-full">
            <thead className="bg-gray-50">
              <tr>
              
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Tenant Name</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Approval Status</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Phone</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">House Number</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Paid Amount</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Payment Details</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Payment Date</th>
                {/* <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Invoice Amount</th> */}
                {/* <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Invoice Status</th> */}
                {/* <th className="px-4 py-3 font-semibold text-gray-500 text-xs text-left uppercase">Actions</th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} />
                ))
              ) : currentItems?.length > 0 ? (
                currentItems.map(tenant => (
                  <BulkPaymentsTennatRow
                    key={tenant.id}
                    tenant={tenant}
                    selected={selectedTenants.includes(tenant.id)}
                    onSelect={handleSelectTenant}
                    onApprove={handleSingleApprove}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="py-6 text-gray-400 text-center">
                    {searchTerm || apartmentFilter || dateFilter
                      ? 'No approvals found matching your filters'
                      : 'No approvals available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && filteredTenants?.length > itemsPerPage && (
          <div className="flex md:flex-row flex-col justify-between items-center gap-2 mt-6">
            <button
              className="bg-white hover:bg-blue-50 disabled:opacity-50 px-4 py-2 border border-gray-300 rounded-md text-blue-600"
              onClick={prevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="text-gray-600 text-sm">
              Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredTenants?.length)} of {filteredTenants?.length}
            </span>
            <button
              className="bg-white hover:bg-blue-50 disabled:opacity-50 px-4 py-2 border border-gray-300 rounded-md text-blue-600"
              onClick={nextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-blue-600 text-white">
          <Modal.Title>
            {paymentStatus === 'approval' ? `Confirm Payment Approval` : `Confirm Payment Rejection`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-5 text-center">
          <div className="mx-auto mb-3 text-center">
            <FaFileInvoice size={48} className="mb-2 text-blue-500" />
          </div>
          <h5 className="mb-3 font-semibold text-gray-800">
            {paymentStatus === 'approval' ? `Approve` : `Reject`}  {selectedTenants.length} Selected Payment(s)?
          </h5>
          <p className="text-gray-600">
            {paymentStatus === 'approval' ? `This will create transaction records and mark them as approved.` : `This will not create transaction records and will mark the payments as rejected.`}
            <br />
            Are you sure you want to continue?
          </p>
        </Modal.Body>
        <Modal.Footer className="flex justify-between">
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)} disabled={isApproving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="bg-blue-600"
            onClick={() => handleApprovePayment(selectedTenants)}
            disabled={isApproving}
          >
            {isApproving ? 'Processing...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Date Filter Modals */}
      <DateFilterModal
        show={showMonthFilterModal}
        onHide={() => setShowMonthFilterModal(false)}
        onApply={handleDateFilterApply}
        filterType="month"
      />
      <DateFilterModal
        show={showYearFilterModal}
        onHide={() => setShowYearFilterModal(false)}
        onApply={handleDateFilterApply}
        filterType="year"
      />
    </div>
  );
};

export default BulkPaymentsTable;